import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { useTenant } from '../context/TenantContext.jsx';
import { formatBrl } from '../utils/currency.js';
import { Link } from 'react-router-dom';
import { OrdersFilterBar } from '../components/orders/OrdersFilterBar.jsx';
import {
  buildOrderFilterStats,
  createDefaultOrderListFilters,
} from '../utils/orderFilters.js';
import {
  ORDER_STATUS_BUTTON_ORDER,
  orderIsPaidViaOnlineGateway,
  orderStatusLabelPtBr,
  orderOnlinePaymentEffectiveState,
  paymentStateChipDescriptor,
} from '../utils/orderStatus.js';
import { OrderErpObsInfoButton } from '../components/orders/OrderErpObsInfoButton.jsx';

function EyeIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path
        d="M2 12s3.8-6 10-6 10 6 10 6-3.8 6-10 6-10-6-10-6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function PrintIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path
        d="M6 9V3h12v6M6.5 17H5a2 2 0 0 1-2-2v-4a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v4a2 2 0 0 1-2 2h-1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="7" y="14" width="10" height="7" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.2" cy="11.2" r="0.9" fill="currentColor" />
    </svg>
  );
}

/**
 * Chip dinâmico do estado do pagamento online (database/0044_payment_state.sql).
 * Substitui o antigo botão "🔄 MP" — agora o rótulo, cor e comportamento mudam
 * conforme o `payment_state` efectivo do pedido:
 *   awaiting_gateway → laranja "Aguardando MP" (clicar = reconciliar agora)
 *   expired          → cinza   "MP expirou"    (informativo)
 *   failed           → vermelho "MP recusado"  (informativo)
 *   paid             → não renderiza (o botão $ Online verde já comunica)
 *   sem dados        → não renderiza (retro-compat com pedidos pré-feature)
 */
function PaymentStateChip({ order, onReconcile, busy }) {
  const desc = paymentStateChipDescriptor(order);
  if (!desc) return null;
  const isClickable = Boolean(desc.clickable && onReconcile);
  const tone = desc.tone;
  // Paleta consistente com o resto do painel:
  //   warn   → âmbar (pulsa devagar para chamar atenção)
  //   muted  → cinza neutro (acção já tomada pelo cron)
  //   danger → vermelho suave (acção já tomada pelo cron, mas com gravidade)
  const toneClass =
    tone === 'warn'
      ? 'border-amber-500/45 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-300/35 dark:bg-amber-950/25 dark:text-amber-100 dark:hover:bg-amber-900/35'
      : tone === 'danger'
      ? 'border-rose-500/45 bg-rose-50 text-rose-900 dark:border-rose-300/35 dark:bg-rose-950/25 dark:text-rose-100'
      : 'border-ink/15 bg-ink/[0.03] text-ink/65 dark:border-white/15 dark:bg-white/5 dark:text-frost-muted';
  const cursorClass = isClickable
    ? 'cursor-pointer transition hover:-translate-y-[1px] hover:shadow-sm'
    : 'cursor-default';
  // Pulso suave (Tailwind default `animate-pulse`) só na variação "Aguardando MP" —
  // o lojista vê de relance que há acção pendente. Outras variações são estáticas.
  const pulseClass = tone === 'warn' ? 'animate-pulse' : '';
  const label = busy ? '⏳ Verificando…' : `${desc.icon} ${desc.label}`;
  const handle = isClickable && !busy ? () => onReconcile(order.id) : undefined;
  return (
    <button
      type="button"
      onClick={handle}
      disabled={!isClickable || busy}
      title={desc.tooltip}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-sm disabled:opacity-65 ${toneClass} ${cursorClass} ${pulseClass}`}
    >
      {label}
    </button>
  );
}

function parseOrderItems(order) {
  const raw = order?.items;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function orderItemsSubtotal(items) {
  return items.reduce((acc, it) => acc + Number(it?.qty || 0) * Number(it?.preco || 0), 0);
}

/** Taxa de entrega gravada no pedido (`delivery_fee`); 0 se ausente ou retirada na loja. */
function orderDeliveryFee(order) {
  const raw = order?.delivery_fee;
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * Resumo financeiro para ficha/modal — usa colunas do pedido quando existem
 * (subtotal, desconto, frete) e faz fallback seguro para pedidos antigos.
 */
function orderPricingSummary(order, itemsTotal = 0) {
  const total = Number(order?.total || 0);
  const deliveryFee = orderDeliveryFee(order);
  const subtotalBefore =
    order?.subtotal_before_discount != null && order?.subtotal_before_discount !== ''
      ? Number(order.subtotal_before_discount)
      : null;
  const discountPct =
    order?.discount_percent != null && order?.discount_percent !== ''
      ? Number(order.discount_percent)
      : null;
  const hasStoredSubtotal = Number.isFinite(subtotalBefore) && subtotalBefore >= 0;
  const hasDiscount =
    hasStoredSubtotal && Number.isFinite(discountPct) && discountPct > 0;

  let productsAfterDiscount;
  if (hasStoredSubtotal) {
    const pct = hasDiscount ? discountPct : 0;
    productsAfterDiscount = Math.round(subtotalBefore * (1 - pct / 100) * 100) / 100;
  } else if (itemsTotal > 0) {
    productsAfterDiscount = itemsTotal;
  } else {
    productsAfterDiscount = Math.max(0, Math.round((total - deliveryFee) * 100) / 100);
  }

  const discountAmount = hasDiscount
    ? Math.max(0, Math.round((subtotalBefore - productsAfterDiscount) * 100) / 100)
    : 0;

  const zoneRaw = order?.delivery_zone_name;
  const deliveryZoneName =
    zoneRaw != null && String(zoneRaw).trim() !== '' ? String(zoneRaw).trim() : null;

  return {
    total,
    deliveryFee,
    productsAfterDiscount,
    subtotalBefore: hasDiscount ? subtotalBefore : null,
    discountPct: hasDiscount ? discountPct : null,
    discountAmount,
    deliveryZoneName,
  };
}

function OrderTotalsBreakdown({ order }) {
  const items = parseOrderItems(order);
  const pricing = orderPricingSummary(order, orderItemsSubtotal(items));
  const deliveryLabel = pricing.deliveryZoneName
    ? `Taxa de entrega (${pricing.deliveryZoneName})`
    : 'Taxa de entrega';

  return (
    <div className="mt-4 space-y-1 rounded-xl border border-ink/10 bg-white/80 p-3 text-sm dark:border-white/10 dark:bg-white/5">
      {pricing.subtotalBefore != null && pricing.discountPct != null ? (
        <>
          <p className="flex justify-between gap-3">
            <span>Subtotal</span>
            <span className="font-semibold tabular-nums">{formatBrl(pricing.subtotalBefore)}</span>
          </p>
          <p className="flex justify-between gap-3 text-emerald-800 dark:text-emerald-200">
            <span>Desconto ({pricing.discountPct}%)</span>
            <span className="font-semibold tabular-nums">−{formatBrl(pricing.discountAmount)}</span>
          </p>
        </>
      ) : null}
      <p className="flex justify-between gap-3">
        <span>Valor dos produtos</span>
        <span className="font-semibold tabular-nums">{formatBrl(pricing.productsAfterDiscount)}</span>
      </p>
      <p className="flex justify-between gap-3">
        <span>{deliveryLabel}</span>
        <span className="font-semibold tabular-nums">{formatBrl(pricing.deliveryFee)}</span>
      </p>
      <p className="flex justify-between gap-3 border-t border-ink/10 pt-2 text-base font-bold dark:border-white/10">
        <span>Total</span>
        <span className="tabular-nums">{formatBrl(pricing.total)}</span>
      </p>
    </div>
  );
}

/**
 * Escape HTML para evitar quebras quando nomes/observações têm `<`, `>`, `&`.
 * O conteúdo entra no DOM da janela de impressão como innerHTML.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Ficha de impressão optimizada para impressoras térmicas 80mm (testada com
 * Elgin i8). Decisões de layout:
 *   • largura útil 72mm — o resto fica para margem física do papel;
 *   • Arial (sans-serif) em vez de Courier — letra preta nítida sem serifa
 *     que borra na cabeça térmica;
 *   • base 13px com headings 16–22px — suficiente para leitura à distância
 *     de balcão sem encaixar mal em 80mm;
 *   • sem bordas no contentor (a borda da i8 já é a borda do papel);
 *   • linhas separadoras feitas com `<hr>` em vez de bordas dashed nos itens
 *     para não duplicar pixels (térmicas reforçam o ponto);
 *   • o item ocupa uma linha de descrição + linha "Nx · R$ unit" alinhada à
 *     direita com o total — facilita conferir;
 *   • a taxa de entrega quebra em duas linhas quando há zona, evitando o
 *     wrap horrível "PARQUE TRINDADE II)" que partia o nome ao meio.
 */
function openPrintView(order) {
  const customerWhatsapp = String(order?.customer_telefone || order?.telefone || '—');
  const customerEmail = String(order?.customer_email || order?.email || '—');
  const customerAddress = String(order?.customer_endereco || order?.endereco || '—');
  const addressNumber = String(order?.entrega_numero || '—');
  const addressComplement = String(order?.entrega_complemento || '—');
  const storeName = String(order?.tenant_nome || 'OminIA Pedidos');
  const items = parseOrderItems(order);
  const when = order?.created_at ? new Date(String(order.created_at).replace(' ', 'T')) : null;
  const whenText = when && !Number.isNaN(when.getTime()) ? when.toLocaleString('pt-BR') : '—';
  const pricing = orderPricingSummary(order, orderItemsSubtotal(items));
  const discountRows =
    pricing.subtotalBefore != null && pricing.discountPct != null
      ? `<div class="trow"><span>Subtotal</span><span>${escapeHtml(formatBrl(pricing.subtotalBefore))}</span></div>
      <div class="trow"><span>Desconto (${escapeHtml(pricing.discountPct)}%)</span><span>-${escapeHtml(formatBrl(pricing.discountAmount))}</span></div>`
      : '';
  const deliveryRow = pricing.deliveryZoneName
    ? `<div class="trow"><span>Taxa de entrega</span><span>${escapeHtml(formatBrl(pricing.deliveryFee))}</span></div>
       <div class="trow-sub">${escapeHtml(pricing.deliveryZoneName)}</div>`
    : `<div class="trow"><span>Taxa de entrega</span><span>${escapeHtml(formatBrl(pricing.deliveryFee))}</span></div>`;

  const itemsHtml =
    items.length > 0
      ? items
          .map((it) => {
            const qty = Number(it?.qty || 0);
            const unit = Number(it?.preco || 0);
            const total = qty * unit;
            return `<div class="item">
              <div class="item-name">${escapeHtml(it?.nome || 'Item')}</div>
              <div class="item-line">
                <span class="item-qty">${qty} x ${escapeHtml(formatBrl(unit))}</span>
                <span class="item-total">${escapeHtml(formatBrl(total))}</span>
              </div>
            </div>`;
          })
          .join('')
      : '<div class="item"><div class="item-name">Sem itens detalhados.</div></div>';

  const html = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>Pedido #${escapeHtml(order.id)}</title>
  <style>
    @page { size: 80mm auto; margin: 2mm 1mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      color: #000;
      background: #fff;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 1.32;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .sheet {
      width: 72mm;
      margin: 0 auto;
      padding: 0;
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: 700; }
    .store {
      text-align: center;
      font-weight: 800;
      font-size: 20px;
      margin: 0 0 1mm;
    }
    .subtitle {
      text-align: center;
      font-weight: 700;
      font-size: 12px;
      letter-spacing: .12em;
      margin: 0 0 2mm;
    }
    .pedido-num {
      text-align: center;
      font-size: 22px;
      font-weight: 800;
      margin: 1mm 0 .5mm;
    }
    .meta-when {
      text-align: center;
      font-size: 12px;
      margin: 0 0 1mm;
    }
    .status-pill {
      display: inline-block;
      font-size: 12px;
      font-weight: 700;
      border: 1px solid #000;
      padding: .4mm 2mm;
      border-radius: 2mm;
      margin: 0 auto 2mm;
    }
    hr {
      border: 0;
      border-top: 1px dashed #000;
      margin: 2mm 0;
    }
    .block p {
      margin: 0 0 .8mm;
      font-size: 13px;
      word-break: break-word;
    }
    .block p .label {
      font-weight: 700;
    }
    .item {
      margin-bottom: 1.5mm;
    }
    .item-name {
      font-weight: 700;
      font-size: 13px;
      word-break: break-word;
    }
    .item-line {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 2mm;
      font-size: 13px;
    }
    .item-qty { color: #000; }
    .item-total { font-weight: 700; }
    .totals { margin-top: 1mm; }
    .totals .trow {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 2mm;
      font-size: 13px;
      margin-bottom: .8mm;
    }
    .totals .trow-sub {
      font-size: 11px;
      margin: -.4mm 0 .8mm;
      padding-left: 1mm;
      font-style: italic;
    }
    .totals .final {
      border-top: 1px solid #000;
      padding-top: 1.2mm;
      margin-top: 1mm;
      font-size: 16px;
      font-weight: 800;
    }
    .obs {
      font-size: 13px;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .foot {
      text-align: center;
      font-size: 11px;
      margin-top: 2mm;
    }
    @media print {
      .no-print { display: none !important; }
    }
    .actions {
      text-align: center;
      margin: 4mm 0;
    }
    .actions button {
      font-size: 13px;
      padding: 6px 14px;
      border-radius: 6px;
      border: 1px solid #000;
      background: #000;
      color: #fff;
      cursor: pointer;
      font-weight: 700;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="store">${escapeHtml(storeName)}</div>
    <div class="subtitle">RESUMO DO PEDIDO</div>
    <div class="pedido-num">Pedido #${escapeHtml(order.id)}</div>
    <div class="meta-when">${escapeHtml(whenText)}</div>
    <div class="center"><span class="status-pill">${escapeHtml(orderStatusLabelPtBr(order?.status || 'pending'))}</span></div>
    <hr />
    <div class="block">
      <p><span class="label">Cliente:</span> ${escapeHtml(order?.customer_nome || '—')}</p>
      <p><span class="label">WhatsApp:</span> ${escapeHtml(customerWhatsapp)}</p>
      <p><span class="label">E-mail:</span> ${escapeHtml(customerEmail)}</p>
      <p><span class="label">Endereço:</span> ${escapeHtml(customerAddress)}</p>
      <p><span class="label">Número:</span> ${escapeHtml(addressNumber)}</p>
      <p><span class="label">Complemento:</span> ${escapeHtml(addressComplement)}</p>
      <p><span class="label">Forma de pagto.:</span> ${escapeHtml(order?.payment_method_code || '—')}</p>
    </div>
    <hr />
    <div class="block">
      ${itemsHtml}
    </div>
    <hr />
    <div class="totals">
      ${discountRows}
      <div class="trow"><span>Produtos</span><span>${escapeHtml(formatBrl(pricing.productsAfterDiscount))}</span></div>
      ${deliveryRow}
      <div class="trow final"><span>TOTAL</span><span>${escapeHtml(formatBrl(pricing.total))}</span></div>
    </div>
    <hr />
    <div class="obs"><span class="bold">Obs:</span> ${escapeHtml(order?.observacoes || '—')}</div>
    <hr />
    <div class="foot">Pedido feito em<br />${escapeHtml(window.location.host)}</div>
  </div>
  <div class="actions no-print">
    <button type="button" onclick="window.print()">Imprimir novamente</button>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=420,height=760');
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  // Pequeno delay para o WebKit/Blink medir as fontes antes de chamar print().
  // Sem isto, a primeira impressão por vezes sai com fontes empilhadas mal nas
  // térmicas (especialmente Chromium 12x).
  setTimeout(() => {
    try {
      win.print();
    } catch (e) {
      void e;
    }
  }, 180);
}

function orderIsPendingOnlinePayment(order) {
  // Mantemos o nome (compat com chamadores existentes), mas agora delegamos no
  // helper unificado que entende o novo `payment_state`: só `awaiting_gateway`
  // é "pendente accionável" — `expired`/`failed` já têm decisão tomada pelo cron.
  return orderOnlinePaymentEffectiveState(order) === 'awaiting_gateway';
}

function formatOrderWhen(createdAt) {
  const raw = String(createdAt || '').trim();
  if (!raw) return '';
  const t = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(t.getTime())) return raw.slice(0, 16);
  return t.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OrdersPage() {
  const { tenantId } = useTenant();
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [previewOrder, setPreviewOrder] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileBusyOrderId, setReconcileBusyOrderId] = useState(null);
  const [reconcileNotice, setReconcileNotice] = useState('');
  const [filters, setFilters] = useState(createDefaultOrderListFilters);

  const filterStats = useMemo(
    () => buildOrderFilterStats(orders, filters),
    [orders, filters],
  );
  const visibleOrders = filterStats.filtered;

  useEffect(() => {
    setFilters(createDefaultOrderListFilters());
  }, [tenantId]);

  async function load() {
    if (!tenantId) return;
    const data = await api(`/tenants/${tenantId}/orders`);
    setOrders(data.orders || []);
  }

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [tenantId]);

  async function changeStatus(orderId, status) {
    setError('');
    try {
      await api(`/tenants/${tenantId}/orders/${orderId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function reconcileAllMp() {
    if (!tenantId || reconcileBusy) return;
    setError('');
    setReconcileNotice('');
    setReconcileBusy(true);
    try {
      const r = await api(`/tenants/${tenantId}/orders/mp/reconcile`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const checked = Number(r?.checked || 0);
      const upApproved = Number(r?.updated_approved || 0);
      const upOther = Number(r?.updated_other || 0);
      const noPay = Number(r?.no_payment_found || 0);
      const errs = Number(r?.errors || 0);
      // Mensagem em linguagem natural para o lojista: o que efectivamente
      // aconteceu no clique. `verificado` ≠ `confirmado`. `outros estados` =
      // pendente/rejeitado/expirado vindo do MP. `sem pagamento` = preferência
      // criada mas cliente nem chegou a pagar.
      const parts = [];
      if (checked === 0) {
        parts.push('Nenhum pedido com pagamento online pendente para verificar.');
      } else {
        parts.push(`${checked} pedido(s) consultado(s) no Mercado Pago`);
        if (upApproved > 0) {
          parts.push(`${upApproved} confirmado(s) como pago(s)`);
        } else {
          parts.push('0 pagamentos confirmados');
        }
        if (upOther > 0) parts.push(`${upOther} ainda aguardando ou recusado(s)`);
        if (noPay > 0) parts.push(`${noPay} cliente(s) ainda sem tentar pagar`);
      }
      if (errs > 0) parts.push(`${errs} erro(s) ao consultar`);
      setReconcileNotice(parts.join(' · '));
      await load();
    } catch (e) {
      setError(e.message || 'Falha ao sincronizar com o Mercado Pago.');
    } finally {
      setReconcileBusy(false);
    }
  }

  async function reconcileOneMp(orderId) {
    if (!tenantId || reconcileBusyOrderId) return;
    setError('');
    setReconcileNotice('');
    setReconcileBusyOrderId(orderId);
    try {
      const r = await api(`/tenants/${tenantId}/orders/${orderId}/mp/reconcile`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (r?.result === 'no_payment_found') {
        setReconcileNotice(`Pedido #${orderId}: nenhum pagamento encontrado no Mercado Pago.`);
      } else if (r?.result === 'updated') {
        const st = String(r?.payment_status || '').toLowerCase();
        setReconcileNotice(
          st === 'approved'
            ? `Pedido #${orderId}: pagamento APROVADO sincronizado (id ${r.payment_id}).`
            : `Pedido #${orderId}: estado «${st}» gravado (id ${r.payment_id}).`,
        );
      } else {
        setReconcileNotice(`Pedido #${orderId}: ${r?.result || 'verificado'}.`);
      }
      await load();
    } catch (e) {
      setError(e.message || 'Falha ao sincronizar este pedido com o Mercado Pago.');
    } finally {
      setReconcileBusyOrderId(null);
    }
  }

  async function loadOrderDetail(orderId) {
    const data = await api(`/tenants/${tenantId}/orders/${orderId}`);
    return data?.order || null;
  }

  async function handlePreview(order) {
    setError('');
    try {
      setDetailLoadingId(order.id);
      const detailed = await loadOrderDetail(order.id);
      setPreviewOrder(detailed || order);
    } catch (e) {
      setError(e.message || 'Não foi possível carregar detalhes do pedido.');
    } finally {
      setDetailLoadingId(null);
    }
  }

  async function handlePrint(order) {
    setError('');
    try {
      setDetailLoadingId(order.id);
      const detailed = await loadOrderDetail(order.id);
      openPrintView(detailed || order);
    } catch (e) {
      setError(e.message || 'Não foi possível carregar dados para impressão.');
    } finally {
      setDetailLoadingId(null);
    }
  }

  if (!tenantId) {
    return (
      <div className="py-12 text-center text-sm text-ink/60 dark:text-frost-muted">
        Selecione uma empresa em{' '}
        <Link className="font-semibold text-accent" to="/app/empresa">
          Empresas
        </Link>
        .
      </div>
    );
  }

  const pendingOnlineCount = orders.filter(orderIsPendingOnlinePayment).length;

  return (
    <div className="space-y-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl font-semibold">Pedidos</h2>
          <p className="text-sm text-ink/60 dark:text-frost-muted">
            Fluxo sugerido: <span className="text-ink/75 dark:text-mist/75">pendente</span> →{' '}
            <span className="text-ink/75 dark:text-mist/75">enviado ao sistema da loja</span> →{' '}
            <span className="text-ink/75 dark:text-mist/75">faturado</span>
            {' — ou '}
            <span className="text-ink/75 dark:text-mist/75">cancelado</span>.{' '}
            <strong className="font-semibold text-ink/65 dark:text-frost-muted">$ Online</strong> acende quando o pagamento foi
            confirmado pelo gateway da loja (ex.: Mercado Pago).
          </p>
        </div>
        <button
          type="button"
          onClick={reconcileAllMp}
          disabled={reconcileBusy}
          title={
            pendingOnlineCount > 0
              ? `Consultar o Mercado Pago para os ${pendingOnlineCount} pedido(s) online que ainda não têm confirmação registrada e atualizar «$ Online».`
              : 'Consultar o Mercado Pago e atualizar o estado dos pedidos pagos online dos últimos 60 dias.'
          }
          className="inline-flex items-center gap-2 rounded-full border border-emerald-600/35 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-900 shadow-sm transition hover:-translate-y-[1px] hover:border-emerald-600/60 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-300/30 dark:bg-emerald-950/30 dark:text-emerald-100 dark:hover:bg-emerald-900/40"
        >
          {reconcileBusy ? '⏳ Sincronizando…' : '🔄 Sincronizar Mercado Pago'}
          {pendingOnlineCount > 0 && !reconcileBusy ? (
            <span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {pendingOnlineCount}
            </span>
          ) : null}
        </button>
      </div>
      {error && <p className="text-sm font-semibold text-accent2">{error}</p>}
      {reconcileNotice && (
        <p className="rounded-2xl border border-emerald-400/35 bg-emerald-50/80 px-4 py-2 text-sm font-semibold text-emerald-900 dark:border-emerald-300/30 dark:bg-emerald-950/30 dark:text-emerald-100">
          {reconcileNotice}
        </p>
      )}
      <OrdersFilterBar
        filters={filters}
        onChange={setFilters}
        statusCounts={filterStats.statusCounts}
        paymentMethods={filterStats.paymentMethods}
        shownCount={visibleOrders.length}
        totalCount={filterStats.total}
      />
      <div className="space-y-3">
        {visibleOrders.map((o) => (
          <div
            key={o.id}
            className="rounded-3xl border border-ink/10 bg-white/80 px-5 py-4 shadow-md dark:border-white/10 dark:bg-white/5"
          >
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-ink/50 dark:text-frost-muted">
                  Pedido #{o.id}
                </div>
                <div className="font-semibold">{o.customer_nome}</div>
                {o.created_at ? (
                  <div className="text-[10px] text-ink/45 dark:text-mist/45">{formatOrderWhen(o.created_at)}</div>
                ) : null}
                <div className="text-xs text-ink/50 dark:text-frost-muted">
                  Total {formatBrl(o.total || 0)}
                  {o.payment_method_code ? (
                    <span className="mt-0.5 block font-mono text-[10px] text-ink/45 dark:text-mist/45">
                      Pag. {o.payment_method_code}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                <button
                  type="button"
                  title="Ver resumo do pedido"
                  disabled={detailLoadingId === o.id}
                  onClick={() => handlePreview(o)}
                  className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-emerald-600/30 bg-white text-emerald-800 shadow-sm transition hover:-translate-y-[1px] hover:border-emerald-600/55 hover:bg-emerald-50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 disabled:cursor-not-allowed disabled:opacity-45 dark:border-emerald-300/30 dark:bg-emerald-950/25 dark:text-emerald-200 dark:hover:bg-emerald-900/35"
                >
                  {detailLoadingId === o.id ? '…' : <EyeIcon className="h-4.5 w-4.5 transition group-hover:scale-110" />}
                </button>
                <button
                  type="button"
                  title="Imprimir pedido"
                  disabled={detailLoadingId === o.id}
                  onClick={() => handlePrint(o)}
                  className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-sky-600/30 bg-white text-sky-800 shadow-sm transition hover:-translate-y-[1px] hover:border-sky-600/55 hover:bg-sky-50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/45 disabled:cursor-not-allowed disabled:opacity-45 dark:border-sky-300/30 dark:bg-sky-950/25 dark:text-sky-200 dark:hover:bg-sky-900/35"
                >
                  {detailLoadingId === o.id ? '…' : <PrintIcon className="h-4.5 w-4.5 transition group-hover:scale-110" />}
                </button>
                {ORDER_STATUS_BUTTON_ORDER.map((st) => {
                  // Quando o botão é o «Cancelado» E o pedido foi cancelado
                  // automaticamente pelo cron de reconciliação MP (payment_state
                  // 'expired'/'failed') — distinguimos visualmente do cancelamento
                  // manual: pequena etiqueta inline `· MP expirou` / `· MP recusado`
                  // + tooltip enriquecido. Para qualquer outro caso, comportamento
                  // antigo intacto.
                  const isCancelActive = st === 'cancelled' && o.status === 'cancelled';
                  const effOnline = isCancelActive ? orderOnlinePaymentEffectiveState(o) : '';
                  const autoCancelTag =
                    effOnline === 'expired'
                      ? 'MP expirou'
                      : effOnline === 'failed'
                      ? 'MP recusado'
                      : '';
                  let title;
                  if (st === 'paid') {
                    title =
                      'Faturado — marcado pelo ERP após emissão da nota fiscal (ou manualmente aqui). Independe do pagamento online, que aparece no botão «$ Online».';
                  } else if (st === 'cancelled' && autoCancelTag) {
                    title =
                      autoCancelTag === 'MP expirou'
                        ? 'Cancelado automaticamente: cliente foi para o Mercado Pago e não pagou dentro do prazo. Estoque devolvido e itens repostos na sacola do cliente.'
                        : 'Cancelado automaticamente: o Mercado Pago recusou o pagamento (cartão negado, fundos insuficientes, etc.). Estoque devolvido.';
                  } else {
                    title = `Estado interno: ${st}`;
                  }
                  return (
                    <button
                      key={st}
                      type="button"
                      title={title}
                      onClick={() => changeStatus(o.id, st)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        o.status === st
                          ? 'bg-accent text-white'
                          : 'border border-ink/10 dark:border-white/10'
                      }`}
                    >
                      {orderStatusLabelPtBr(st)}
                      {autoCancelTag ? (
                        <span className="ml-1 text-[10px] font-normal opacity-80">
                          · {autoCancelTag}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
                <button
                  type="button"
                  disabled={!orderIsPaidViaOnlineGateway(o)}
                  title={
                    orderIsPaidViaOnlineGateway(o)
                      ? 'Pagamento online confirmado pelo gateway (ex.: Mercado Pago). O status «Faturado» será marcado pelo ERP após emissão da nota fiscal.'
                      : 'Disponível quando o gateway online (ex.: Mercado Pago) confirmar o pagamento. O status «Faturado» é independente — só o ERP o marca via API.'
                  }
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    orderIsPaidViaOnlineGateway(o)
                      ? 'bg-emerald-600 text-white dark:bg-emerald-500'
                      : 'cursor-not-allowed border border-ink/10 opacity-45 dark:border-white/10'
                  }`}
                >
                  $ Online
                </button>
                <PaymentStateChip
                  order={o}
                  onReconcile={reconcileOneMp}
                  busy={reconcileBusyOrderId === o.id}
                />
                <OrderErpObsInfoButton erpObs={o.erp_obs} />
              </div>
            </div>
          </div>
        ))}
        {!visibleOrders.length && orders.length > 0 && !error && (
          <p className="text-sm text-ink/50 dark:text-frost-muted">
            Nenhum pedido corresponde aos filtros actuais. Ajuste a pesquisa ou use «Limpar filtros».
          </p>
        )}
        {!orders.length && !error && (
          <p className="text-sm text-ink/50 dark:text-frost-muted">Nenhum pedido ainda.</p>
        )}
      </div>

      {previewOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => setPreviewOrder(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-emerald-400/45 bg-gradient-to-br from-white to-emerald-50 p-5 shadow-2xl dark:border-emerald-400/35 dark:from-[#141b17] dark:to-[#0f1713]"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="rounded-2xl border-2 border-dashed border-emerald-400/70 bg-white/80 p-5 dark:bg-white/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-ink/45 dark:text-mist/45">Resumo do pedido</p>
                  <h3 className="font-display text-2xl font-bold text-ink dark:text-mist">🧾 Pedido #{previewOrder.id}</h3>
                </div>
                <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-900 dark:text-emerald-200">
                  {orderStatusLabelPtBr(previewOrder.status)}
                  {orderIsPaidViaOnlineGateway(previewOrder) ? ' · $ Online' : ''}
                </span>
              </div>

              <div className="mt-4 grid gap-2 rounded-xl border border-ink/10 bg-white/80 p-3 text-sm dark:border-white/10 dark:bg-white/5">
                <p>👤 <strong>Cliente:</strong> {previewOrder.customer_nome || '—'}</p>
                <p>📞 <strong>WhatsApp:</strong> {previewOrder.customer_telefone || previewOrder.telefone || '—'}</p>
                <p>📧 <strong>E-mail:</strong> {previewOrder.customer_email || previewOrder.email || '—'}</p>
                <p>📍 <strong>Endereço:</strong> {previewOrder.customer_endereco || previewOrder.endereco || '—'}</p>
                <p>🔢 <strong>Número:</strong> {previewOrder.entrega_numero || '—'}</p>
                <p>🏷️ <strong>Complemento:</strong> {previewOrder.entrega_complemento || '—'}</p>
              </div>

              <div className="mt-4">
                <p className="mb-2 text-sm font-semibold">📦 Itens</p>
                <ul className="space-y-2">
                  {parseOrderItems(previewOrder).length > 0 ? (
                    parseOrderItems(previewOrder).map((it, idx) => {
                      const qty = Number(it?.qty || 0);
                      const unit = Number(it?.preco || 0);
                      return (
                        <li
                          key={`${previewOrder.id}-item-${idx}`}
                          className="rounded-xl border border-ink/10 bg-white/80 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
                        >
                          • {qty}x {String(it?.nome || 'Item')} — {formatBrl(qty * unit)}
                        </li>
                      );
                    })
                  ) : (
                    <li className="rounded-xl border border-ink/10 bg-white/80 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5">
                      • Itens não detalhados neste pedido.
                    </li>
                  )}
                </ul>
              </div>

              <OrderTotalsBreakdown order={previewOrder} />

              <div className="mt-4 rounded-xl border border-ink/10 bg-white/80 p-3 text-sm dark:border-white/10 dark:bg-white/5">
                📝 <strong>Observações:</strong> {previewOrder.observacoes || '—'}
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => openPrintView(previewOrder)}
                  className="rounded-2xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white dark:bg-emerald-600"
                >
                  🖨 Imprimir ficha
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewOrder(null)}
                  className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold dark:border-white/20"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
